import { useState, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { EmployeeDetailHeader } from '@/presentation/components/EmployeeDetailHeader';
import { DateRangeFilter } from '@/presentation/components/DateRangeFilter';
import { useTimelogsByAccessRoleQuery } from '@/modules/attendance/application/timelogQueries';
import type { Timelog } from '@/modules/attendance/domain/entities/Timelog';
import type { Employee } from '@/modules/employees/domain/entities/Employee';
import { useEmployeeByEntityQuery } from '@/modules/employees/application/employeeQueries';
import { TimelogListItem } from '@/modules/employees/presentation/components/TimelogListItem';
import { TimelogDetailModal } from '@/modules/employees/presentation/components/TimelogDetailModal';
import { GlassLoader } from '@/shared/ui/components/GlassLoader';
import { defaultRange } from '@/shared/utils/datetime';
import { MethodRow } from '@/modules/admin/presentation/components/MethodRow';
import { DeleteMethodConfirmModal } from '@/modules/admin/presentation/components/DeleteMethodConfirmModal';
import {
  useDeleteFacialEmbeddingMutation,
  useDeleteNfcBiometricMutation,
} from '@/modules/admin/application/enrollmentDeletionMutations';
import { useNfcBiometricId } from '@/modules/admin/application/hooks/useNfcBiometricId';

type ConfirmDelete = { kind: 'facial' | 'nfc'; label: string };

export function AdminEmployeeDetailPage() {
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { idEntity } = useParams<{ idEntity: string }>();
  const { selectedRole } = useProfile();
  const { t } = useTranslation('common');

  // Initial snapshot from route state — used as a fallback before the live query resolves.
  const initialEmployee = (location.state as { employee?: Employee } | null)?.employee ?? null;
  const idEntityNumber = idEntity !== undefined ? Number(idEntity) : null;

  // Live query — drives the UI. Composite repo (Dexie-backed) means it updates
  // immediately after mutations invalidate employeeKeys.all. This is the fix
  // for "UI doesn't refresh after delete" — the previous implementation read
  // emp from location.state once and never refreshed.
  const employeeQuery = useEmployeeByEntityQuery(idEntityNumber);
  const emp: Employee | null = employeeQuery.data ?? initialEmployee;

  // ── Date range + timelog query (UNCHANGED) ────────────────────────────────
  const __dr = defaultRange();
  const [from, setFrom] = useState(__dr.from);
  const [to, setTo] = useState(__dr.to);
  const [detail, setDetail] = useState<Timelog | null>(null);

  const { data: allTimelogs = [], isFetching } = useTimelogsByAccessRoleQuery(
    selectedRole?.idAccessRole ?? null, from, to,
  );

  const filtered = useMemo(() => {
    if (!emp) return allTimelogs;
    return allTimelogs.filter((tl) => tl.idEntity === Number(idEntity));
  }, [allTimelogs, emp, idEntity]);

  // ── Delete state ──────────────────────────────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState<ConfirmDelete | null>(null);
  // OLD: offlineNotice state for offline delete guard (no longer needed)
  // const [offlineNotice, setOfflineNotice] = useState<string | null>(null);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const facialMutation = useDeleteFacialEmbeddingMutation();
  const nfcMutation = useDeleteNfcBiometricMutation();

  // ── NFC biometric id (Dexie, async) ───────────────────────────────────────
  const { nfcBiometricId } = useNfcBiometricId(emp?.idPerson ?? null);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleDeleteClick = (kind: 'facial' | 'nfc', label: string) => {
    // OFFLINE-FIRST UPDATE (2026-07-23): Removed offline guard for facial deletion.
    // Facial deletion now works offline (enqueues sync). NFC still requires online
    // for now (can be enabled later following the same pattern).
    // 
    // OLD GUARD (commented for rollback):
    // if (!navigator.onLine) {
    //   setOfflineNotice(t('admin.employeeDetail.offlineDeleteBlocked', { method: label }));
    //   setTimeout(() => { setOfflineNotice(null); }, 3000);
    //   return;
    // }
    
    setConfirmDelete({ kind, label });
  };

  const handleDeleteConfirm = () => {
    if (!emp || !confirmDelete) return;
    if (confirmDelete.kind === 'facial') {
      facialMutation.mutate(
        { idEntity: emp.idEntity, idPerson: emp.idPerson },
        { onSuccess: () => setConfirmDelete(null) },
      );
    } else {
      if (nfcBiometricId === null) return;
      nfcMutation.mutate(
        { idEntity: emp.idEntity, idPerson: emp.idPerson, idEntityBiometric: nfcBiometricId },
        { onSuccess: () => setConfirmDelete(null) },
      );
    }
  };

  // ── Navigation handlers (LOCKED paths — verbatim from original Change C) ──
  const handleFacialNav = () => {
    void navigate(`/admin/empleados/${emp?.idPerson}/enroll/photo`, {
      state: { employee: emp, idEntity: emp?.idEntity },
    });
  };
  const handlePinNav = () => {
    void navigate(`/admin/empleados/${emp?.idEntity}/enroll/pin`);
  };
  const handleCardNav = () => {
    void navigate(`/admin/empleados/${emp?.idPerson}/enroll/card`);
  };

  // ── i18n helpers ──────────────────────────────────────────────────────────
  const enrolledBadge = t('admin.employeeDetail.enrolledBadge');
  const notEnrolledBadge = t('admin.employeeDetail.notEnrolledBadge');
  const enrollTooltip = t('admin.employeeDetail.enrollTooltip');
  const updateTooltip = t('admin.employeeDetail.updateTooltip');
  const deleteTooltip = t('admin.employeeDetail.deleteTooltip');
  const nfcDisabledTooltip = t('admin.employeeDetail.deleteNfcDisabledTooltip');

  const facialLabel = t('admin.employeeDetail.facialSubtitle');
  const nfcLabel = t('admin.employeeDetail.nfcSubtitle');

  return (
    <div style={{ flex: 1, background: T.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* ── Shared header (prototype-faithful centered layout) ─────────────── */}
      <EmployeeDetailHeader
        initials={emp?.initials ?? ''}
        avatarColor={emp?.avatarColor ?? C.primary}
        photoUrl={emp?.photoUrl ?? null}
        fullName={emp?.fullName ?? '—'}
        cargo={emp?.cargo ?? null}
        dept={emp?.dept ?? null}
        onBack={() => { void navigate('/admin/empleados'); }}
        title={t('admin.employeeDetailTitle')}
        headerTestId="admin-employee-header"
        titleTestId="admin-employee-detail-title"
        cargoTestId="admin-employee-cargo"
        deptTestId="admin-employee-dept"
        T={T}
        C={C}
      />

      {/* ── Methods + Timelog (scrollable body) ──────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '22px 30px 30px' }}>
        <div style={{ maxWidth: 645, margin: '0 auto' }}>

          {/* Methods section */}
          <div data-testid="admin-methods-section">
            {/* Section title */}
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>
              {t('admin.employeeDetail.methodsTitle')}
            </div>

            {/* OLD: Offline notice for delete guard (no longer needed - offline deletion enabled)
            {offlineNotice !== null && (
              <div
                role="alert"
                style={{
                  marginBottom: 12,
                  padding: '10px 14px',
                  borderRadius: 9,
                  background: `${C.danger}12`,
                  border: `1px solid ${C.danger}33`,
                  color: C.danger,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {offlineNotice}
              </div>
            )}
            */}

            {emp && (
              <>
                {/* Facial */}
                <MethodRow
                  kind="facial"
                  title={t('admin.employeeDetail.facialSubtitle')}
                  subtitle="Marcaje con el rostro del empleado"
                  color={C.blue}
                  isEnrolled={emp.enrolledMethods.face}
                  onEnroll={handleFacialNav}
                  onUpdate={handleFacialNav}
                  onDelete={() => { handleDeleteClick('facial', facialLabel); }}
                  enrolledBadge={enrolledBadge}
                  notEnrolledBadge={notEnrolledBadge}
                  enrollTooltip={enrollTooltip}
                  updateTooltip={updateTooltip}
                  deleteTooltip={deleteTooltip}
                  T={T}
                  C={C}
                />

                {/* PIN — no delete */}
                <MethodRow
                  kind="pin"
                  title={t('admin.employeeDetail.pinSubtitle')}
                  subtitle="PIN de 4 dígitos con ID o documento"
                  color={C.success}
                  isEnrolled={emp.enrolledMethods.pin}
                  onEnroll={handlePinNav}
                  onUpdate={handlePinNav}
                  enrolledBadge={enrolledBadge}
                  notEnrolledBadge={notEnrolledBadge}
                  enrollTooltip={enrollTooltip}
                  updateTooltip={updateTooltip}
                  T={T}
                  C={C}
                />

                {/* NFC/Card */}
                <MethodRow
                  kind="nfc"
                  title={t('admin.employeeDetail.nfcSubtitle')}
                  subtitle="Credencial NFC asociada al empleado"
                  color={C.teal}
                  isEnrolled={emp.enrolledMethods.card}
                  onEnroll={handleCardNav}
                  onUpdate={handleCardNav}
                  onDelete={() => { handleDeleteClick('nfc', nfcLabel); }}
                  deleteDisabled={nfcBiometricId === null}
                  deleteDisabledTooltip={nfcDisabledTooltip}
                  enrolledBadge={enrolledBadge}
                  notEnrolledBadge={notEnrolledBadge}
                  enrollTooltip={enrollTooltip}
                  updateTooltip={updateTooltip}
                  deleteTooltip={deleteTooltip}
                  T={T}
                  C={C}
                />
              </>
            )}
          </div>

          {/* ── Timelog section (UNCHANGED logic) — prototype margins ──────── */}
          <div style={{ margin: '26px 0 18px' }}>
            <DateRangeFilter
              value={{ from, to }}
              onChange={({ from: newFrom, to: newTo }) => {
                setFrom(newFrom);
                setTo(newTo);
              }}
              label={t('admin.employeeDetailTimelogTitle')}
            />
          </div>
          <div style={{ position: 'relative', overflow: 'hidden' }}>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '45px 0', color: T.textMuted, fontSize: 11 }}>
                {t('admin.employeeDetailNoRecords')}
              </div>
            ) : (
              filtered.map((tl) => (
                <TimelogListItem
                  key={tl.idTimelog}
                  timelog={tl}
                  initials={emp?.initials ?? '??'}
                  avatarColor={emp?.avatarColor ?? C.primary}
                  onClick={() => { setDetail(tl); }}
                  T={T}
                  C={C}
                />
              ))
            )}
            {isFetching && <GlassLoader T={T} />}
          </div>
        </div>
      </div>

      {/* ── Modals ────────────────────────────────────────────────────────────── */}
      {detail && emp && (
        <TimelogDetailModal
          timelog={detail}
          initials={emp.initials}
          avatarColor={emp.avatarColor}
          onClose={() => { setDetail(null); }}
          T={T}
          C={C}
        />
      )}

      {confirmDelete !== null && emp !== null && (
        <DeleteMethodConfirmModal
          method={{ kind: confirmDelete.kind, label: confirmDelete.label }}
          employeeName={emp.fullName}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={handleDeleteConfirm}
          isPending={
            confirmDelete.kind === 'facial' ? facialMutation.isPending : nfcMutation.isPending
          }
          cancelLabel={t('admin.cancel')}
          confirmLabel={t('admin.employeeDetail.deleteConfirmButton')}
          pendingLabel={t('admin.employeeDetail.deletingButton')}
          T={T}
          C={C}
        />
      )}
    </div>
  );
}
